import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import EmptyState from '../../components/EmptyState';
import TourButton from '../../components/TourButton';
import { usePageTour } from '../../hooks/usePageTour';
import PermissionGuard from '../../components/PermissionGuard';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { grnService } from '../../services/grnService';
import { GRNListDTO, GRNStatus, GRNApprovalStatus, GRNType } from '../../types';
import './GRNPage.css';
import { CirclePlus, Eye, Printer, RefreshCw, SquarePen, FileText, PenLine, Clock, CheckCircle2, Check, AlertCircle } from 'lucide-react';

// ─── Badge maps ───────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<GRNStatus, string> = {
  Draft:     'ims-badge ims-badge-draft',
  Received:  'ims-badge ims-badge-received',
  Verified:  'ims-badge ims-badge-verified',
  Completed: 'ims-badge ims-badge-completed',
};

const APPROVAL_BADGE: Record<GRNApprovalStatus, string> = {
  Pending:  'ims-badge ims-badge-pending',
  Approved: 'ims-badge ims-badge-approved',
  Rejected: 'ims-badge ims-badge-rejected',
};

const TYPE_BADGE: Record<GRNType, string> = {
  Direct: 'ims-badge ims-badge-direct',
  WithPO: 'ims-badge ims-badge-withpo',
};

// ─── Component ────────────────────────────────────────────────────────────────

const GRNListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [grns, setGrns] = useState<GRNListDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [, setPrintingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as any)?.message || null
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadGRNs(); }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadGRNs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await grnService.getGRNs(1, 100);
      const payload = res.data?.data;
      if (payload) setGrns(payload.data ?? []);
    } catch {
      setError(t('grn.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await grnService.getGRNs(1, 100);
      const payload = res.data?.data;
      if (payload) setGrns(payload.data ?? []);
    } catch {
      setError(t('grn.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  // ── Stat counts ────────────────────────────────────────────────────────────
  const totalCount    = grns.length;
  const draftCount    = grns.filter((g) => g.status === 'Draft').length;
  const pendingCount  = grns.filter((g) => g.approvalStatus === 'Pending').length;
  const approvedCount = grns.filter((g) => g.approvalStatus === 'Approved').length;

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns: GridColumn[] = [
    {
      key: 'grnNumber',
      label: t('grn.grnNumber'),
      sortable: true,
      width: '140px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'grnDate',
      label: t('grn.date'),
      sortable: true,
      width: '120px',
      render: (value) => new Date(value).toLocaleDateString(langCode),
    },
    {
      key: 'supplierName',
      label: t('grn.supplier'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value || 'N/A'}</strong>,
    },
    {
      key: 'warehouseName',
      label: t('grn.warehouse'),
      sortable: true,
      width: '150px',
      hideTablet: true,
    },
    {
      key: 'grnType',
      label: t('grn.type'),
      sortable: true,
      width: '110px',
      render: (value: GRNType) => (
        <span className={TYPE_BADGE[value] ?? 'ims-badge ims-badge-direct'}>
          {value === 'Direct' ? t('grn.typeBadgeDirect') : t('grn.typeBadgeWithPO')}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('common.status'),
      sortable: true,
      width: '110px',
      render: (value: GRNStatus) => (
        <span className={STATUS_BADGE[value] ?? 'ims-badge ims-badge-draft'}>
          {t(`grn.status${value}`)}
        </span>
      ),
    },
    {
      key: 'approvalStatus',
      label: t('grn.approval'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (value: GRNApprovalStatus) => (
        <span className={APPROVAL_BADGE[value] ?? 'ims-badge ims-badge-pending'}>
          {t(`grn.approval${value}`)}
        </span>
      ),
    },
    {
      key: 'itemCount',
      label: t('grn.items'),
      sortable: true,
      width: '72px',
      hideMobile: true,
    },
  ];

  const handlePrint = async (item: GRNListDTO) => {
    setPrintingId(item.grnId);
    setError(null);
    try {
      await grnService.openPdfInNewTab(item.grnId);
    } catch {
      setError(t('grn.failedToPrint'));
    } finally {
      setPrintingId(null);
    }
  };

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      onClick: (item: GRNListDTO) => navigate(`/grn/${item.grnId}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.GRN.Edit)
      ? [{
          icon: <SquarePen size={16} strokeWidth={1.75} />,
          onClick: (item: GRNListDTO) => navigate(`/grn/${item.grnId}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
          hidden: (item: GRNListDTO) =>
            item.approvalStatus === 'Approved' || item.status === 'Completed',
        }]
      : []),
    {
      icon: <Printer size={16} strokeWidth={1.75} />,
      onClick: handlePrint,
      className: 'grid-action-btn-print',
      title: t('grn.printPdf'),
    },
  ];

  const { startTour } = usePageTour('grn', [
    {
      element: '[data-tour="grn-new"]',
      popover: {
        title: t('tour.grn.newTitle') || 'Receive goods',
        description: t('tour.grn.newText')
          || 'When a supplier delivery arrives, record it as a GRN (Goods Received Note) — it adds the items into your stock.',
      },
    },
    {
      element: '[data-tour="grn-stats"]',
      popover: {
        title: t('tour.grn.statsTitle') || 'Receiving overview',
        description: t('tour.grn.statsText')
          || 'How many deliveries are drafted, pending approval or completed.',
      },
    },
    {
      element: '[data-tour="grn-grid"]',
      popover: {
        title: t('tour.grn.gridTitle') || 'Past deliveries',
        description: t('tour.grn.gridText')
          || 'Search past GRNs, open one to check its lines or print it.',
      },
    },
  ], !loading);

  return (
    <Layout>
      <div className="grn-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('grn.title')}</h1>
          <div className="ims-list-actions">
            <TourButton onClick={startTour} />
            <PermissionGuard claim={Permissions.GRN.Create}>
              <button
                className="btn-new"
                data-tour="grn-new"
                onClick={() => navigate('/grn/create')}
              >
                <CirclePlus/>
                {t('grn.createGRN')}
              </button>
            </PermissionGuard>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'ims-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="ims-stats-row" data-tour="grn-stats">
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-total">
              <FileText size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{totalCount}</span>
              <span className="ims-stat-label">{t('grn.title')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-draft">
              <PenLine size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{draftCount}</span>
              <span className="ims-stat-label">{t('grn.statusDraft')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-pending">
              <Clock size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{pendingCount}</span>
              <span className="ims-stat-label">{t('grn.approvalPending')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-approved">
              <CheckCircle2 size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{approvedCount}</span>
              <span className="ims-stat-label">{t('grn.approvalApproved')}</span>
            </div>
          </div>
        </div>

        {/* ── Alerts ── */}
        {successMessage && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 16 }}>
            <Check size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{successMessage}</span>
            <button
              type="button"
              onClick={() => setSuccessMessage(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >✕</button>
          </div>
        )}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >✕</button>
          </div>
        )}

        {/* ── DataGrid ── */}
        <div className="ims-grid-wrapper" data-tour="grn-grid">
          <DataGrid
            data={grns}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('grn.noGRNs')}
            emptyState={(
              <EmptyState
                icon={<FileText size={44} strokeWidth={1.2} />}
                title={t('emptyState.grn.title') || 'No goods received yet'}
                description={t('emptyState.grn.text')
                  || 'A GRN (Goods Received Note) records a supplier delivery and puts the items into stock. Record your first delivery when goods arrive.'}
                actionLabel={t('grn.createGRN')}
                onAction={() => navigate('/grn/create')}
              />
            )}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>

      </div>
    </Layout>
  );
};

export default GRNListPage;
